// CIM Command Center → Book tab (#/cim/<engagementId>?tab=book[&doc=<n>]) —
// the composed book of one deal (routes/cim_book.py, design pr4_7 §8.9, PR 7).
//
//   Settings     the cover line and the band; Edit with can_render
//   Render book  the composition preview first (snapshot, Book / Draft, one
//                row per section: version, Approved / Draft / Missing /
//                Suppressed, Current), then Render (can_render: the deal
//                team, an admin or a dealmaker)
//   Rendering    progress lines polled every 5 s (window.usePolling,
//                cacheMs: 0) until the job ends, and Cancel
//   Documents    v, Book / Draft, created, by, buyer PDF Passed / Blocked / —,
//                PowerPoint Released / Held / —
//   Document     files (a download is a signed URL the server logs; the buyer
//                PDF only when it passed, the PowerPoint only when released),
//                the buyer PDF's final gate report and the PowerPoint's, each
//                failure on its page (p. N · §NN, opens that page below), and
//                the 1× page images
//   (7c, design pr4_7 §8.6–§8.9)
//   Buyer PDF    the deliverable now (Atlas / Team upload), Use Atlas's PDF,
//                and its history (every switch, by whom, why)
//   Team PDFs    Upload PDF (desktop; a drop zone; it REPLACES the buyer PDF),
//                each upload with its audit (Queued / Running / Done /
//                Unreadable / Failed / Stopped; names as blockers, contacts,
//                active content, hygiene, numbers as information, by page or
//                where the file carries them), Use this, Audit again; while an
//                audit is queued or running its job is polled every 5 s
//                (cacheMs: 0; the light job read, no access log), the
//                document read again when one ends, never polled after
//   Final approval  the state (Approved / replaced / Not approved), the final
//                checks (Pass / Fail / Not run, Blocks / Warning), Final approve
//                (a tick per warning, a reason when an audit didn't run) and
//                Withdraw (a reason) for an admin or a dealmaker
//
// Labels only; timestamps, never durations or spend. Gate messages, audit
// findings and section titles are shown as plain text (never as HTML). Ids
// arrive as names (page numbers, file names, versions, people).
//
// window.CimBookTab({ engagementId, phone })
// window.CimBookKit — the pure rules below (atlas-crm/test/cim_book.test.mjs)

const BK_JOB = {
  queued: ['Queued', 'slate'], running: ['Running', 'blue'], validating: ['Validating', 'amber'],
  completed: ['Completed', 'green'], failed: ['Failed', 'rose'], cancelled: ['Cancelled', 'slate'],
};
const BK_KIND = { book: ['Book', 'blue'], draft: ['Draft', 'amber'] };
const BK_BUYER = { passed: ['Passed', 'green'], blocked: ['Blocked', 'rose'] };
const BK_DECK = { released: ['Released', 'green'], held: ['Held', 'amber'] };
const BK_SECTION = {
  approved: ['Approved', 'green'], draft: ['Draft', 'amber'], missing: ['Missing', 'slate'], suppressed: ['Suppressed', 'slate'],
};
const BK_CHECKS = [
  ['mandate_signed', 'Mandate signed'], ['sections_approved', 'Sections approved'], ['sections_current', 'Sections current'],
  ['claims_validated', 'Claims validated'], ['cutoff_consistent', 'Data cutoff'], ['deal_killers_covered', 'Deal killers covered'],
  ['references', 'References'],
];
const BK_CHECK_STATE = { pass: ['Pass', 'green'], fail: ['Fail', 'rose'], not_run: ['Not run', 'slate'] };
const BK_ACTIVE = new Set(['queued', 'running', 'validating']);
const BK_TERMINAL = new Set(['completed', 'failed', 'cancelled']);
const BK_SOURCE = { atlas: ['Atlas', 'blue'], upload: ['Team upload', 'violet'] };
const BK_FINAL = { approved: ['Approved', 'green'], replaced: ['Replaced', 'amber'] };
const BK_AUDIT = {
  queued: ['Queued', 'slate'], running: ['Running', 'blue'], done: ['Done', 'green'], unreadable: ['Unreadable', 'amber'],
  failed: ['Failed', 'rose'], stopped: ['Stopped', 'slate'],
};
const BK_AUDIT_ACTIVE = new Set(['queued', 'running']);
// Where a finding was, beyond a page's text (the audit child's `where`).
const BK_WHERE = {
  properties: 'Properties', xmp: 'Properties', bookmarks: 'Bookmarks', annotations: 'Comments', forms: 'Form',
  links: 'Link', attachments: 'Attachment', file: 'File',
};
const BK_PDF_MAX = 50 * 1024 * 1024;
const BK_MIN_REASON = 3;

const _bkCard = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10 };
const _bkCap = { fontSize: 10.5, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-4)' };
const _bkInput = {
  width: '100%', fontSize: 12.5, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)',
  background: 'var(--surface)', color: 'var(--text)', boxSizing: 'border-box', fontFamily: 'inherit',
};
const _bkLink = { border: 'none', background: 'transparent', padding: 0, font: 'inherit', color: 'var(--accent-text)', cursor: 'pointer', textAlign: 'left' };

// ── pure rules (window.CimBookKit) ─────────────────────────────────────────

// apiFetch throws "409 Conflict: {"detail": …}"; the detail is the server's sentence. An
// object (final approval's {blocks, warnings, acknowledge}) comes back whole as `body`.
const bkErr = (e) => {
  const msg = (e && e.message) || '';
  const m = /^(\d{3})\b[^:]*:\s*([\s\S]*)$/.exec(msg);
  if (!m) return { status: null, detail: msg || 'Something went wrong', body: null };
  let detail = m[2].trim();
  let body = null;
  try {
    const j = JSON.parse(detail);
    if (j && typeof j.detail === 'string') detail = j.detail;
    else if (j && Array.isArray(j.detail)) detail = (j.detail[0] && j.detail[0].msg) || 'Not valid';
    else if (j && j.detail && typeof j.detail === 'object') { body = j.detail; detail = 'The checks changed'; }
  } catch (_) { /* not JSON */ }
  return { status: Number(m[1]), detail: detail || `Error ${m[1]}`, body };
};

// Why Render is off, as a label, or null when it may run.
const bkRenderGate = (B) => {
  if (!B) return { ok: false, why: null };
  if (!B.enabled) return { ok: false, why: 'Switched off' };
  if (!B.can_render) return { ok: false, why: 'Deal team, admin or dealmaker' };
  if (B.active_job) return { ok: false, why: 'Rendering' };
  const p = (B.preview && B.preview.problems) || [];
  if (p.length) return { ok: false, why: p[0] };
  return { ok: true, why: null };
};

// [label, hue] of a document's buyer PDF and PowerPoint states; '—' when there is none.
const bkState = (map, k) => (k && map[k]) || ['—', null];

// The server's gate report → groups of failures, gate by gate (passing gates left out).
const bkFailureGroups = (report) => ((report && report.gates) || [])
  .filter((g) => (g.failures || []).length > 0)
  .map((g) => ({ gate: g.gate, name: g.name, rows: g.failures }));

// A check in plain words (the Sections tab's words, views/cim_sections.jsx); its code and
// the report's name go in a tooltip. Without that kit, the report's name.
const bkGateWord = (gate, name) => {
  const k = window.CimSectionsKit;
  return k && k.gateWord ? k.gateWord(gate) : (name || gate);
};

// "p. 4 · §11" for a failure on a page; "Book" for one with no page.
const bkPageLabel = (f) => {
  if (!f) return '';
  if (f.page_no != null && f.page_no !== '') return `p. ${f.page_no}${f.section ? ` · §${f.section}` : ''}`;
  return !f.page || f.page === '*' ? 'Book' : String(f.page);
};

// The settings PATCH body: only what changed, trimmed ('' clears a field).
const bkSettingsPatch = (settings, draft) => {
  const s = settings || {};
  const out = {};
  ['descriptor', 'band'].forEach((k) => {
    const next = String((draft && draft[k]) || '').trim();
    if (next !== String(s[k] || '').trim()) out[k] = next;
  });
  return out;
};

const bkMergeEvents = (cur, next) => {
  const seen = new Set((cur || []).map((e) => e.id));
  return [...(cur || []), ...(next || []).filter((e) => !seen.has(e.id))];
};

// The document to show: the one the link names (?doc=<n>), else the newest.
const bkPickDoc = (docs, version) => {
  const list = docs || [];
  if (version != null) {
    const hit = list.find((d) => String(d.version) === String(version));
    if (hit) return hit;
  }
  return list[0] || null;
};

const bkDocFromHash = () => {
  try {
    const v = new URLSearchParams((window.location.hash.split('?')[1] || '')).get('doc');
    return v && /^\d+$/.test(v) ? Number(v) : null;
  } catch (_) {
    return null;
  }
};

// A check's details on one line: the first four, then +N.
const bkDetail = (detail) => {
  const d = detail || [];
  return d.slice(0, 4).join(' · ') + (d.length > 4 ? ` +${d.length - 4}` : '');
};

// A document whose render did not finish (cancelled, failed) or is still going: its job's pill.
const bkDocJob = (d) => (d && d.job_status && d.job_status !== 'completed' ? BK_JOB[d.job_status] || null : null);

// The files the tab lists, each with what its row shows.
const bkFileRows = (files) => (files || []).map((f) => ({
  ...f,
  stateLabel: f.kind === 'buyer_pdf' ? bkState(BK_BUYER, f.passed ? 'passed' : 'blocked')
    : f.kind === 'pptx' ? bkState(BK_DECK, f.passed ? 'released' : 'held') : null,
  where: f.filed ? f.name : 'Not filed',
}));

// Signed page-image URLs fetched at `at` (ms) for `ttl` (ms) count as expired a minute before
// they run out; an image that fails while they are fresh is not refetched.
const bkPagesExpired = (at, ttl, now) => ttl > 0 && now - at >= Math.max(0, ttl - 60000);

const _bkWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const t = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return d.toDateString() === new Date().toDateString() ? t : `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, ${t}`;
};
const _bkClock = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', second: '2-digit' });
};
const _bkSize = (n) => (n == null ? '' : n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

const _bkStamp = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
};
const _bkN = (n, one, many) => `${n} ${n === 1 ? one : (many || `${one}s`)}`;

// ── 7c: the buyer PDF, the team uploads, final approval ─────────────────────

// Why a file can't be sent as the team PDF (the server checks again, and reads the bytes).
const bkPdfRefusal = (file) => {
  if (!file) return 'Upload a PDF';
  if (!/\.pdf$/i.test(String(file.name || ''))) return 'Upload a PDF';
  if (!(file.size > 0)) return 'Upload a PDF';
  if (file.size > BK_PDF_MAX) return 'The PDF is over 50 MB';
  return null;
};

// "4 numbers · 0 names · 1 hygiene" for an audit that ran (contacts and active
// content when there are any); '' otherwise.
const bkAuditCounts = (u) => {
  if (!u || u.audit_state !== 'done') return '';
  const a = u.audit || {};
  const n = (k) => Number((a[k] || {}).count || 0);
  return [_bkN(n('numbers'), 'number'), _bkN(n('names'), 'name'), n('contacts') ? _bkN(n('contacts'), 'contact') : null,
    n('active') ? `${n('active')} active` : null, `${n('hygiene')} hygiene`].filter(Boolean).join(' · ');
};

// Where a finding was: "p. 2", or the part of the file ("Properties", "Comments · p. 3").
const bkWhere = (f) => {
  const w = f && f.where && f.where !== 'page' ? (BK_WHERE[f.where] || 'File') : null;
  const p = f && f.page != null && f.page !== '' ? `p. ${f.page}` : null;
  return [w, p].filter(Boolean).join(' · ') || '—';
};

// An upload's findings: names first (they block final approval), then contacts, active
// content and hygiene (warnings), then numbers not in the snapshot (information).
const bkFindingRows = (u) => {
  const a = (u && u.audit) || {};
  const rows = [];
  const add = (k, kind, text, blocks) => ((a[k] || {}).findings || []).forEach((f) => rows.push({
    kind, page: f.page, where: bkWhere(f), text: text(f), blocks,
  }));
  add('names', 'name', (f) => f.msg, true);
  add('contacts', 'contact', (f) => f.msg, false);
  add('active', 'active', (f) => f.msg, false);
  add('hygiene', 'hygiene', (f) => f.msg, false);
  add('numbers', 'number', (f) => f.number, false);
  return rows;
};

// The audit jobs to follow while an upload's audit is queued or running (job ids).
const bkAuditJobs = (doc) => ((doc && doc.uploads) || [])
  .filter((u) => BK_AUDIT_ACTIVE.has(u.audit_state)).map((u) => u.job_id);

// Whether an upload's audit is still to run: the tab follows its job until none is.
const bkAuditing = (doc) => ((doc && doc.uploads) || []).some((u) => BK_AUDIT_ACTIVE.has(u.audit_state));

// The final approval's state line.
const bkApprovalLine = (ap) => {
  if (!ap || ap.state === 'none' || !ap.state) return 'Not approved';
  if (ap.state === 'approved') return `Approved${ap.by ? ` by ${ap.by}` : ''}${ap.at ? ` · ${_bkStamp(ap.at)}` : ''}`;
  return `Final approval was for the file replaced on ${_bkStamp(ap.replaced_at)}`;
};

// The final checks as rows: label, Pass / Fail / Not run, and Blocks / Warning.
const bkFinalRows = (final) => {
  const f = final || {};
  const blocking = new Set((f.blocks || []).map((b) => b.id));
  const dlWarn = (f.warnings || []).some((w) => /^(upload|atlas):/.test(w.id));
  return (f.checks || []).map((c) => ({
    id: c.id, label: c.label, pair: bkState(BK_CHECK_STATE, c.status), detail: bkDetail(c.detail),
    tag: blocking.has(c.id) ? 'Blocks'
      : ((!c.blocks && c.status !== 'pass') || (c.id === 'deliverable_ok' && dlWarn)) ? 'Warning' : null,
  }));
};

// What Final approve may send: no blocker, every warning ticked, a reason where one is needed.
const bkApproveReady = (final, ticked, reason) => {
  const ws = (final && final.warnings) || [];
  const needsReason = ws.some((w) => w.needs_reason);
  const allTicked = ws.every((w) => ticked.has(w.id));
  const reasonOk = !needsReason || String(reason || '').trim().length >= BK_MIN_REASON;
  return { ok: ((final && final.blocks) || []).length === 0 && allTicked && reasonOk, needsReason, allTicked, reasonOk };
};

// The final approval's body: the current file (its event and exact sha) and the ticks.
const bkApproveBody = (doc, ids, reason) => {
  const d = (doc && doc.deliverable) || {};
  const r = String(reason || '').trim();
  return { action: 'final_approve', deliverable_event_id: d.event_id == null ? null : d.event_id, sha256: d.sha256 || null,
    acknowledge: ids, ...(r ? { reason: r } : {}) };
};

// Atlas's buyer PDF when it may become the buyer PDF again (it passed and isn't now).
const bkAtlasChoice = (doc) => {
  const f = ((doc && doc.files) || []).find((x) => x.kind === 'buyer_pdf');
  if (!f || !f.passed) return null;
  if (doc.deliverable && doc.deliverable.source === 'atlas') return null;
  return { source: 'atlas', id: f.id, label: "Atlas's PDF", name: f.name };
};

window.CimBookKit = Object.freeze({
  err: bkErr, renderGate: bkRenderGate, state: bkState, failureGroups: bkFailureGroups, pageLabel: bkPageLabel,
  settingsPatch: bkSettingsPatch, mergeEvents: bkMergeEvents, pickDoc: bkPickDoc, fileRows: bkFileRows, docJob: bkDocJob, detail: bkDetail,
  pagesExpired: bkPagesExpired, pdfRefusal: bkPdfRefusal, auditCounts: bkAuditCounts, findingRows: bkFindingRows,
  where: bkWhere, auditJobs: bkAuditJobs, auditing: bkAuditing, approvalLine: bkApprovalLine, finalRows: bkFinalRows, approveReady: bkApproveReady,
  approveBody: bkApproveBody, atlasChoice: bkAtlasChoice,
  maps: Object.freeze({ BK_BUYER, BK_DECK, BK_KIND, BK_SECTION, BK_SOURCE, BK_FINAL, BK_AUDIT }),
});

// ── pieces ─────────────────────────────────────────────────────────────────

const BkPill = ({ pair, dot = false }) => (pair && pair[1]
  ? <window.Pill hue={pair[1]} sz="sm" dot={dot}>{pair[0]}</window.Pill>
  : <span style={{ fontSize: 12, color: 'var(--text-4)' }}>{(pair && pair[0]) || '—'}</span>);

const BkPanel = ({ title, action, children }) => (
  <section style={{ ..._bkCard, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{title}</h3>
      <span style={{ flex: 1 }} />
      {action}
    </div>
    {children}
  </section>
);

const BkSettings = ({ B, busy, onSave, phone }) => {
  const s = B.settings || {};
  const [edit, setEdit] = React.useState(false);
  const [draft, setDraft] = React.useState({ descriptor: s.descriptor || '', band: s.band || '' });
  React.useEffect(() => { setDraft({ descriptor: s.descriptor || '', band: s.band || '' }); }, [s.descriptor, s.band]);
  const patch = bkSettingsPatch(s, draft);
  const dirty = Object.keys(patch).length > 0;
  const line = (label, value) => (
    <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', minWidth: 0, flexWrap: phone ? 'wrap' : 'nowrap' }}>
      <span style={{ ..._bkCap, width: 84, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, color: value ? 'var(--text)' : 'var(--text-4)', overflowWrap: 'anywhere', minWidth: 0 }}>{value || '—'}</span>
    </div>
  );
  const field = (label, k, max) => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      <span style={_bkCap}>{label}</span>
      <input value={draft[k]} maxLength={max} onChange={(e) => { const v = e.target.value; setDraft((d) => ({ ...d, [k]: v })); }} style={_bkInput} />
    </label>
  );
  return (
    <BkPanel title="Settings"
      action={B.can_render && !edit ? <window.Btn size="sm" variant="ghost" onClick={() => setEdit(true)}>Edit</window.Btn> : null}>
      {!edit ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {line('Cover line', s.descriptor)}
          {line('Band', s.band)}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {field('Cover line', 'descriptor', 140)}
          {field('Band', 'band', 80)}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <window.Btn size="sm" variant="ghost" disabled={busy} onClick={() => { setEdit(false); setDraft({ descriptor: s.descriptor || '', band: s.band || '' }); }}>Cancel</window.Btn>
            <window.Btn size="sm" variant="accent" disabled={busy || !dirty}
              onClick={async () => { if (await onSave(patch)) setEdit(false); }}>Save</window.Btn>
          </div>
        </div>
      )}
    </BkPanel>
  );
};

const BkPreview = ({ B, busy, onRender, onClose, phone }) => {
  const p = B.preview || {};
  const gate = bkRenderGate(B);
  const rows = (p.sections || []);
  return (
    <window.Drawer open onClose={onClose} title="Render book" width={phone ? undefined : 620} widthKey="cim-book-preview"
      footer={(
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
          {!gate.ok && gate.why && <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{gate.why}</span>}
          <window.Btn size="sm" variant="ghost" disabled={busy} onClick={onClose}>Cancel</window.Btn>
          <window.Btn size="sm" variant="accent" disabled={busy || !gate.ok} onClick={onRender}>Render</window.Btn>
        </div>
      )}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <BkPill pair={bkState(BK_KIND, p.kind)} />
          <window.Pill hue="slate" sz="sm">{p.snapshot_version != null ? `Locked numbers v${p.snapshot_version}` : 'Numbers not locked'}</window.Pill>
          <window.Pill hue="slate" sz="sm">{`${p.pages || 0} page${p.pages === 1 ? '' : 's'}`}</window.Pill>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--text-4)' }}>
              <th style={{ ..._bkCap, padding: '4px 6px' }}>§</th>
              {!phone && <th style={{ ..._bkCap, padding: '4px 6px' }}>Section</th>}
              <th style={{ ..._bkCap, padding: '4px 6px' }}>Version</th>
              <th style={{ ..._bkCap, padding: '4px 6px' }}>State</th>
              <th style={{ ..._bkCap, padding: '4px 6px' }}>Current</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.section} style={{ borderTop: '1px solid var(--border)', color: r.state === 'missing' || r.state === 'suppressed' ? 'var(--text-4)' : 'var(--text)' }}>
                <td style={{ padding: '5px 6px', fontVariantNumeric: 'tabular-nums' }}>{r.section}</td>
                {!phone && <td style={{ padding: '5px 6px' }}>{r.title}</td>}
                <td style={{ padding: '5px 6px' }}>{r.version != null ? `v${r.version}` : '—'}</td>
                <td style={{ padding: '5px 6px' }}><BkPill pair={bkState(BK_SECTION, r.state)} /></td>
                <td style={{ padding: '5px 6px' }}>{r.current == null ? '—' : <BkPill pair={r.current ? ['Current', 'green'] : ['Newer data', 'amber']} />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </window.Drawer>
  );
};

const BkLive = ({ live, canCancel, onCancel, phone }) => {
  const job = live.job || {};
  return (
    <div style={{ ..._bkCard, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Render</span>
        <BkPill pair={bkState(BK_JOB, job.status || 'queued')} dot />
        <span style={{ flex: 1 }} />
        {canCancel && <window.Btn size="sm" variant="outline" onClick={onCancel}>Cancel</window.Btn>}
      </div>
      <div role="log" aria-live="polite" style={{ maxHeight: phone ? 220 : 260, overflowY: 'auto', padding: '6px 0', fontSize: 12, lineHeight: 1.5 }}>
        {live.events.length === 0 && <div style={{ padding: '4px 14px', color: 'var(--text-4)' }}>Queued</div>}
        {live.events.map((ev) => (
          <div key={ev.id} style={{ display: 'grid', gridTemplateColumns: '88px 1fr', gap: 8, padding: '1px 14px' }}>
            <span style={{ color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{_bkClock(ev.at)}</span>
            <span style={{ color: 'var(--text-2)', overflowWrap: 'anywhere' }}>{ev.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const BkDocuments = ({ docs, selId, onSelect, phone }) => (
  <BkPanel title="Documents">
    {(docs || []).length === 0 ? <div style={{ fontSize: 12.5, color: 'var(--text-4)' }}>None yet</div> : phone ? (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {docs.map((d) => (
          <button key={d.id} type="button" onClick={() => onSelect(d)}
            style={{ ..._bkCard, display: 'flex', flexDirection: 'column', gap: 6, padding: '8px 10px', textAlign: 'left', cursor: 'pointer',
              borderColor: d.id === selId ? 'var(--accent)' : 'var(--border)', background: 'var(--surface)', color: 'var(--text)' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontWeight: 600, fontSize: 13 }}>{`v${d.version}`}</span>
              <BkPill pair={bkState(BK_KIND, d.kind)} />
              {bkDocJob(d) && <BkPill pair={bkDocJob(d)} />}
              <span style={{ flex: 1 }} />
              <span style={{ fontSize: 11.5, color: 'var(--text-4)' }}>{_bkWhen(d.created_at)}</span>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 12, color: 'var(--text-3)' }}>
              <span>Buyer PDF</span><BkPill pair={bkState(BK_BUYER, d.buyer_pdf)} />
              <span>PowerPoint</span><BkPill pair={bkState(BK_DECK, d.pptx)} />
            </div>
            {(d.deliverable || d.final) && (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 12, color: 'var(--text-3)' }}>
                <span>Deliverable</span><BkPill pair={bkState(BK_SOURCE, d.deliverable)} />
                {d.final && <BkPill pair={bkState(BK_FINAL, d.final)} />}
              </div>
            )}
          </button>
        ))}
      </div>
    ) : (
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
        <thead>
          <tr style={{ textAlign: 'left' }}>
            {['v', 'Kind', 'Created', 'By', 'Buyer PDF', 'PowerPoint', 'Deliverable', 'Final'].map((h) => <th key={h} style={{ ..._bkCap, padding: '4px 6px' }}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {docs.map((d) => (
            <tr key={d.id} onClick={() => onSelect(d)} aria-selected={d.id === selId}
              style={{ borderTop: '1px solid var(--border)', cursor: 'pointer', background: d.id === selId ? 'var(--accent-soft)' : 'transparent' }}>
              <td style={{ padding: '6px', fontWeight: 600 }}>
                <button type="button" style={_bkLink} onClick={(e) => { e.stopPropagation(); onSelect(d); }}>{`v${d.version}`}</button>
              </td>
              <td style={{ padding: '6px' }}>
                <span style={{ display: 'inline-flex', gap: 6 }}>
                  <BkPill pair={bkState(BK_KIND, d.kind)} />
                  {bkDocJob(d) && <BkPill pair={bkDocJob(d)} />}
                </span>
              </td>
              <td style={{ padding: '6px', color: 'var(--text-3)' }}>{_bkWhen(d.created_at)}</td>
              <td style={{ padding: '6px', color: 'var(--text-3)' }}>{d.created_by || '—'}</td>
              <td style={{ padding: '6px' }}><BkPill pair={bkState(BK_BUYER, d.buyer_pdf)} /></td>
              <td style={{ padding: '6px' }}><BkPill pair={bkState(BK_DECK, d.pptx)} /></td>
              <td style={{ padding: '6px' }}><BkPill pair={bkState(BK_SOURCE, d.deliverable)} /></td>
              <td style={{ padding: '6px' }}><BkPill pair={bkState(BK_FINAL, d.final)} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </BkPanel>
);

const BkReport = ({ title, pair, why, report, onPage }) => {
  const groups = bkFailureGroups(report);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{title}</span>
        <BkPill pair={pair} />
      </div>
      {(why || []).map((w) => <div key={w} style={{ fontSize: 12, color: 'var(--amber-fg)' }}>{w}</div>)}
      {groups.map((g) => (
        <div key={g.gate} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div title={`${g.gate} · ${g.name}`} style={{ fontSize: 12, fontWeight: 600, color: 'var(--rose-fg)' }}>{bkGateWord(g.gate, g.name)}</div>
          {g.rows.map((f, i) => (
            <div key={`${g.gate}-${i}`} style={{ display: 'grid', gridTemplateColumns: '96px 1fr', gap: 8, fontSize: 12, lineHeight: 1.45 }}>
              {f.page_no != null
                ? <button type="button" style={_bkLink} onClick={() => onPage(f.page)}>{bkPageLabel(f)}</button>
                : <span style={{ color: 'var(--text-3)' }}>{bkPageLabel(f)}</span>}
              <span style={{ color: 'var(--text-2)', overflowWrap: 'anywhere' }}>{f.msg}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

const BkPages = ({ pages, at, setAt, phone, onExpired }) => {
  if (!pages) return <window.SkeletonRows rows={3} />;
  if (!pages.length) return <div style={{ fontSize: 12.5, color: 'var(--text-4)' }}>No page images</div>;
  const i = Math.min(Math.max(at, 0), pages.length - 1);
  const p = pages[i];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <img src={p.url} alt={`Page ${p.page_no}`} onError={onExpired} style={{ width: '100%', height: 'auto', aspectRatio: '16 / 9', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface-2)' }} />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <window.Btn size="sm" variant="ghost" disabled={i === 0} onClick={() => setAt(i - 1)}>Prev</window.Btn>
        <span style={{ fontSize: 12, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>{`p. ${p.page_no} of ${pages.length}`}</span>
        <window.Btn size="sm" variant="ghost" disabled={i >= pages.length - 1} onClick={() => setAt(i + 1)}>Next</window.Btn>
      </div>
      {!phone && (
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2 }}>
          {pages.map((q, k) => (
            <button key={q.page_id} type="button" onClick={() => setAt(k)} aria-label={`Page ${q.page_no}`}
              style={{ padding: 0, border: `2px solid ${k === i ? 'var(--accent)' : 'transparent'}`, borderRadius: 4, background: 'transparent', cursor: 'pointer', flexShrink: 0 }}>
              <img src={q.url} alt="" onError={onExpired} style={{ width: 96, height: 54, objectFit: 'cover', display: 'block', borderRadius: 2 }} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── 7c: the buyer PDF, the team PDFs, final approval ──────────────────────

const _bkRow = { display: 'flex', gap: 8, alignItems: 'center', minWidth: 0, flexWrap: 'wrap' };
const _bkMuted = { fontSize: 12, color: 'var(--text-3)', overflowWrap: 'anywhere', minWidth: 0 };
const BK_CURRENT = ['Current', 'green'];
const BK_FINDING = { name: 'Name', contact: 'Contact', active: 'Active', hygiene: 'Hygiene', number: 'Number' };

const BkBuyerFile = ({ doc, canSwitch, busy, onDownload, onUse, phone }) => {
  const cur = doc.deliverable;
  const atlas = bkAtlasChoice(doc);
  const history = doc.history || [];
  return (
    <BkPanel title="Buyer PDF" action={cur ? <BkPill pair={bkState(BK_SOURCE, cur.source)} /> : null}>
      {cur ? (
        <div style={_bkRow}>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{cur.label}</span>
          <span style={{ ..._bkMuted, flex: 1 }}>{cur.name}</span>
          <window.Btn size="sm" variant="outline" onClick={() => onDownload({ id: cur.file_id })}>Download</window.Btn>
        </div>
      ) : <div style={{ fontSize: 12.5, color: 'var(--text-4)' }}>None</div>}
      {canSwitch && atlas && (
        <div><window.Btn size="sm" variant="ghost" disabled={busy} onClick={() => onUse(atlas)}>Use Atlas's PDF</window.Btn></div>
      )}
      {history.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={_bkCap}>History</span>
          {history.map((h, i) => (
            <div key={h.id == null ? `atlas-${i}` : h.id}
              style={{ display: 'grid', gridTemplateColumns: phone ? 'minmax(0, 1fr)' : '120px minmax(0, 1fr) auto', gap: phone ? 2 : 8, alignItems: 'baseline', fontSize: 12 }}>
              <span style={{ color: 'var(--text-4)' }}>{_bkStamp(h.at)}</span>
              <span style={{ color: 'var(--text-2)', overflowWrap: 'anywhere' }}>{`${h.label} · ${h.by || '—'}${h.reason ? ` · ${h.reason}` : ''}`}</span>
              {h.current ? <BkPill pair={BK_CURRENT} /> : <span />}
            </div>
          ))}
        </div>
      )}
    </BkPanel>
  );
};

const BkUploads = ({ doc, canUpload, canSwitch, busy, onUpload, onUse, onAuditAgain, onDownload, phone }) => {
  const [open, setOpen] = React.useState(null);
  const [drag, setDrag] = React.useState(false);
  const ups = doc.uploads || [];
  const pick = (files) => {
    const f = files && files[0];
    if (f) onUpload(f);
  };
  return (
    <BkPanel title="Team PDFs">
      {canUpload && !phone && (
        <label data-dropzone=""
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); if (!busy) pick(e.dataTransfer && e.dataTransfer.files); }}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px', borderRadius: 8, fontSize: 12.5,
            border: `1px dashed ${drag ? 'var(--accent)' : 'var(--border)'}`, background: drag ? 'var(--accent-soft)' : 'transparent',
            color: busy ? 'var(--text-4)' : 'var(--accent-text)', cursor: busy ? 'default' : 'pointer' }}>
          <input type="file" accept="application/pdf,.pdf" disabled={busy} style={{ display: 'none' }}
            onChange={(e) => { const fs = e.target.files; pick(fs); try { e.target.value = ''; } catch (_) { /* read-only */ } }} />
          Upload PDF
        </label>
      )}
      {ups.length === 0 ? <div style={{ fontSize: 12.5, color: 'var(--text-4)' }}>None</div> : ups.map((u) => {
        const rows = bkFindingRows(u);
        const counts = bkAuditCounts(u);
        const why = (u.audit_state === 'unreadable' || u.audit_state === 'failed') && u.audit ? u.audit.reason : null;
        return (
          <div key={u.id} data-upload={u.id} style={{ ..._bkCard, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={_bkRow}>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{u.label}</span>
              <BkPill pair={bkState(BK_AUDIT, u.audit_state)} />
              {u.current && <BkPill pair={BK_CURRENT} />}
              <span style={{ flex: 1 }} />
              <span style={{ fontSize: 11.5, color: 'var(--text-4)' }}>{_bkStamp(u.uploaded_at)}</span>
            </div>
            <div style={_bkMuted}>{`${u.name}${u.uploaded_by ? ` · ${u.uploaded_by}` : ''}`}</div>
            {counts && <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{counts}</div>}
            {why && <div style={{ fontSize: 12, color: 'var(--amber-fg)', overflowWrap: 'anywhere' }}>{why}</div>}
            <div style={_bkRow}>
              <window.Btn size="sm" variant="outline" onClick={() => onDownload({ id: u.id })}>Download</window.Btn>
              {canSwitch && !u.current && (
                <window.Btn size="sm" variant="ghost" disabled={busy} onClick={() => onUse({ source: 'upload', id: u.id, label: u.label, name: u.name })}>Use this</window.Btn>
              )}
              {canSwitch && u.can_audit_again && (
                <window.Btn size="sm" variant="ghost" disabled={busy} onClick={() => onAuditAgain(u)}>Audit again</window.Btn>
              )}
              {rows.length > 0 && (
                <window.Btn size="sm" variant="ghost" onClick={() => setOpen(open === u.id ? null : u.id)}>{open === u.id ? 'Hide findings' : 'Findings'}</window.Btn>
              )}
            </div>
            {open === u.id && (
              <div role="list" style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 260, overflowY: 'auto' }}>
                {rows.map((r, i) => (
                  <div role="listitem" key={`${r.kind}-${i}`} data-kind={r.kind}
                    style={{ display: 'grid', gridTemplateColumns: phone ? '76px 60px minmax(0, 1fr)' : '120px 72px minmax(0, 1fr)', gap: 6, fontSize: 12 }}>
                    <span style={{ color: 'var(--text-4)', overflowWrap: 'anywhere' }}>{r.where}</span>
                    <span style={{ color: r.blocks ? 'var(--rose-fg)' : 'var(--text-3)', fontWeight: r.blocks ? 600 : 400 }}>{BK_FINDING[r.kind]}</span>
                    <span style={{ color: 'var(--text-2)', overflowWrap: 'anywhere' }}>{r.text}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </BkPanel>
  );
};

const BkFinal = ({ doc, canApprove, busy, onApprove, onWithdraw, phone }) => {
  const rows = bkFinalRows(doc.final);
  const ap = doc.approval || {};
  const inForce = ap.state === 'approved' || ap.state === 'replaced';
  const action = canApprove ? (
    <span style={{ display: 'inline-flex', gap: 6 }}>
      {inForce && <window.Btn size="sm" variant="ghost" disabled={busy} onClick={onWithdraw}>Withdraw</window.Btn>}
      {ap.state !== 'approved' && (
        <window.Btn size="sm" variant="accent" disabled={busy || !doc.deliverable} onClick={onApprove}>Final approve</window.Btn>
      )}
    </span>
  ) : null;
  return (
    <BkPanel title="Final approval" action={action}>
      <div style={_bkRow}>
        {inForce && <BkPill pair={bkState(BK_FINAL, ap.state)} />}
        <span style={{ fontSize: 12.5, color: 'var(--text-2)', overflowWrap: 'anywhere' }}>{bkApprovalLine(ap)}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={_bkCap}>Final checks</span>
        {rows.map((r) => (
          <div key={r.id} data-check={r.id}
            style={{ display: 'grid', gridTemplateColumns: phone ? 'minmax(0, 1fr) auto auto' : '150px auto auto minmax(0, 1fr)', gap: 8, alignItems: 'baseline', fontSize: 12 }}>
            <span style={{ color: 'var(--text-2)' }}>{r.label}</span>
            <BkPill pair={r.pair} />
            {r.tag ? <BkPill pair={[r.tag, r.tag === 'Blocks' ? 'rose' : 'amber']} /> : <span />}
            {!phone && <span style={{ color: 'var(--text-3)', overflowWrap: 'anywhere' }}>{r.detail}</span>}
          </div>
        ))}
      </div>
    </BkPanel>
  );
};

// Final approve: the file, what blocks (Approve stays off), a tick per warning, a reason when
// an audit didn't run. A 409 hands back the fresh lists (changed), unticked.
const BkApproveDrawer = ({ doc, final, changed, busy, onConfirm, onClose, phone }) => {
  const [ticked, setTicked] = React.useState(() => new Set());
  const [reason, setReason] = React.useState('');
  React.useEffect(() => { setTicked(new Set()); }, [final]);
  const ws = (final && final.warnings) || [];
  const blocks = (final && final.blocks) || [];
  const ready = bkApproveReady(final, ticked, reason);
  const cur = doc.deliverable || {};
  const toggle = (id) => setTicked((c) => {
    const next = new Set(c);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const footer = (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
      <window.Btn size="sm" variant="ghost" disabled={busy} onClick={onClose}>Cancel</window.Btn>
      <window.Btn size="sm" variant="accent" disabled={busy || !ready.ok} onClick={() => onConfirm(ws.map((w) => w.id), reason)}>Approve</window.Btn>
    </div>
  );
  return (
    <window.Drawer open onClose={onClose} title="Final approve" footer={footer} width={phone ? undefined : 560} widthKey="cim-book-approve">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {changed && (
          <div role="status" style={{ ..._bkCard, padding: '8px 12px', fontSize: 12.5, background: 'var(--amber-bg)', color: 'var(--amber-fg)', borderColor: 'transparent' }}>Checks changed</div>
        )}
        <div style={_bkRow}>
          <BkPill pair={bkState(BK_SOURCE, cur.source)} />
          <span style={{ ..._bkMuted, color: 'var(--text)' }}>{cur.name || '—'}</span>
        </div>
        {blocks.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ ..._bkCap, color: 'var(--rose-fg)' }}>{_bkN(blocks.length, 'blocker')}</span>
            {blocks.map((b, i) => <div key={`${b.id}-${i}`} style={{ fontSize: 12.5, color: 'var(--rose-fg)', overflowWrap: 'anywhere' }}>{b.text}</div>)}
          </div>
        )}
        {ws.length > 0 && (
          <fieldset style={{ border: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <legend style={{ ..._bkCap, marginBottom: 6 }}>{_bkN(ws.length, 'warning')}</legend>
            {ws.map((w) => (
              <label key={w.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: phone ? '8px 0' : '5px 0', fontSize: phone ? 14 : 12.5, cursor: 'pointer', color: 'var(--text)' }}>
                <input type="checkbox" checked={ticked.has(w.id)} onChange={() => toggle(w.id)} style={{ marginTop: 3, flexShrink: 0 }} />
                <span style={{ overflowWrap: 'anywhere' }}>{w.text}</span>
              </label>
            ))}
          </fieldset>
        )}
        {ready.needsReason && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={_bkCap}>Reason</span>
            <textarea value={reason} maxLength={500} rows={3} onChange={(e) => setReason(e.target.value)} style={{ ..._bkInput, resize: 'vertical' }} />
          </label>
        )}
      </div>
    </window.Drawer>
  );
};

// Use a file as the buyer PDF, or withdraw final approval: one reason, then confirm.
const BkReasonDrawer = ({ title, subject, confirmLabel, max, busy, onConfirm, onClose, phone }) => {
  const [reason, setReason] = React.useState('');
  const ok = reason.trim().length >= BK_MIN_REASON;
  const footer = (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
      <window.Btn size="sm" variant="ghost" disabled={busy} onClick={onClose}>Cancel</window.Btn>
      <window.Btn size="sm" variant="accent" disabled={busy || !ok} onClick={() => onConfirm(reason.trim())}>{confirmLabel}</window.Btn>
    </div>
  );
  return (
    <window.Drawer open onClose={onClose} title={title} footer={footer} width={phone ? undefined : 480} widthKey="cim-book-reason">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {subject && <div style={{ ..._bkMuted, color: 'var(--text)' }}>{subject}</div>}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={_bkCap}>Reason</span>
          <textarea value={reason} maxLength={max} rows={3} onChange={(e) => setReason(e.target.value)} style={{ ..._bkInput, resize: 'vertical' }} />
        </label>
      </div>
    </window.Drawer>
  );
};

const BkDocument = ({ engagementId, doc, phone, onDownload, B, busy, acts }) => {
  const api = window.AtlasApi;
  const [pages, setPages] = React.useState(null);
  const [at, setAt] = React.useState(0);
  const signed = React.useRef({ at: 0, ttl: 0, load: null });
  const d = doc.document || {};
  React.useEffect(() => {
    let alive = true;
    setPages(null);
    setAt(0);
    const load = () => api.cimBookPages(engagementId, d.id).then((r) => {
      if (!alive) return;
      signed.current = { at: Date.now(), ttl: Number((r && r.expires_in) || 0) * 1000, load };
      setPages((r && r.pages) || []);
    });
    load().catch(() => { if (alive) setPages([]); });
    return () => { alive = false; };
  }, [engagementId, d.id]);
  // An image that won't load once its signed URL ran out: fresh URLs, once per set, same page.
  const onExpired = () => {
    const s = signed.current;
    if (!s.load || !bkPagesExpired(s.at, s.ttl, Date.now())) return;
    signed.current = { ...s, load: null };
    s.load().catch(() => {});
  };
  const goPage = (pageId) => {
    const k = (pages || []).findIndex((p) => p.page_id === pageId);
    if (k >= 0) setAt(k);
  };
  const files = bkFileRows(doc.files);
  const checks = d.checks || {};
  return (
    <div style={{ display: 'grid', gap: 12, gridTemplateColumns: phone ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) minmax(0, 1fr)', alignItems: 'start' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
        <BkPanel title={`v${d.version} files`} action={<BkPill pair={bkState(BK_KIND, d.kind)} />}>
          {files.length === 0 ? <div style={{ fontSize: 12.5, color: 'var(--text-4)' }}>No files</div> : files.map((f) => (
            <div key={f.id} style={{ display: 'flex', gap: 8, alignItems: 'center', minWidth: 0 }}>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', width: phone ? 84 : 92, flexShrink: 0 }}>{f.label}</span>
              {f.stateLabel && <BkPill pair={f.stateLabel} />}
              <span style={{ fontSize: 12, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1 }}>{f.where}</span>
              {!phone && <span style={{ fontSize: 11.5, color: 'var(--text-4)' }}>{_bkSize(f.size_bytes)}</span>}
              {f.downloadable && <window.Btn size="sm" variant="outline" onClick={() => onDownload(f)}>Download</window.Btn>}
            </div>
          ))}
          {doc.pptx_unavailable && !doc.pptx && (
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{`PowerPoint: ${doc.pptx_unavailable}`}</div>
          )}
          {doc.tieout_unavailable && (
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{`Tie-out: ${doc.tieout_unavailable}`}</div>
          )}
        </BkPanel>
        {doc.final && acts && (
          <>
            <BkBuyerFile doc={doc} canSwitch={!!(B && B.can_render)} busy={busy} onDownload={onDownload} onUse={acts.use} phone={phone} />
            <BkUploads doc={doc} canUpload={!!(B && B.can_render && B.enabled)} canSwitch={!!(B && B.can_render)} busy={busy}
              onUpload={acts.upload} onUse={acts.use} onAuditAgain={acts.auditAgain} onDownload={onDownload} phone={phone} />
            <BkFinal doc={doc} canApprove={!!(B && B.can_approve)} busy={busy} onApprove={acts.approve} onWithdraw={acts.withdraw} phone={phone} />
          </>
        )}
        <BkPanel title="Checks">
          {doc.buyer_pdf
            ? <BkReport title="Buyer PDF" pair={bkState(BK_BUYER, doc.buyer_pdf.state)} report={doc.buyer_pdf.report} onPage={goPage} />
            : <BkReport title="Buyer PDF" pair={bkState(BK_BUYER, null)} report={null} onPage={goPage} />}
          {doc.pptx && (
            <BkReport title="PowerPoint" pair={bkState(BK_DECK, doc.pptx.state)} why={doc.pptx.why} report={doc.pptx.report} onPage={goPage} />
          )}
          {!doc.final && <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {BK_CHECKS.filter(([k]) => checks[k]).map(([k, label]) => (
              <div key={k} style={{ display: 'grid', gridTemplateColumns: '150px auto 1fr', gap: 8, alignItems: 'baseline', fontSize: 12 }}>
                <span style={{ color: 'var(--text-2)' }}>{label}</span>
                <BkPill pair={bkState(BK_CHECK_STATE, checks[k].status)} />
                <span style={{ color: 'var(--text-3)', overflowWrap: 'anywhere' }}>{bkDetail(checks[k].detail)}</span>
              </div>
            ))}
          </div>}
        </BkPanel>
      </div>
      <div style={phone ? undefined : { position: 'sticky', top: 0 }}>
        <BkPanel title="Pages">
          <BkPages pages={pages} at={at} setAt={setAt} phone={phone} onExpired={onExpired} />
        </BkPanel>
      </div>
    </div>
  );
};

// ── the tab ────────────────────────────────────────────────────────────────

const CimBookTab = ({ engagementId, phone = false }) => {
  const { useState, useEffect, useRef, useCallback } = React;
  const api = window.AtlasApi;
  const toast = (t) => window.pushToast && window.pushToast(t);
  const [B, setB] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);
  const [live, setLive] = useState(null);
  const [selId, setSelId] = useState(null);
  const [doc, setDoc] = useState(null);
  const [dialog, setDialog] = useState(null);
  const liveRef = useRef(null);
  liveRef.current = live;
  const selRef = useRef(null);
  selRef.current = selId;
  const wanted = useRef(bkDocFromHash());

  const load = useCallback(async () => {
    try {
      const r = await api.cimBook(engagementId);
      setB(r);
      setErr(null);
      if (r.active_job && !liveRef.current) setLive({ jobId: r.active_job.id, job: r.active_job, events: [] });
      return r;
    } catch (e) {
      setErr(bkErr(e));
      return null;
    }
  }, [engagementId]);

  useEffect(() => { load(); }, [load]);

  // the selected document: the link's (?doc=<n>), else the newest
  useEffect(() => {
    if (!B) return;
    const pick = bkPickDoc(B.documents, selId ? null : wanted.current);
    const keep = selId && (B.documents || []).some((d) => d.id === selId);
    if (!keep) setSelId(pick ? pick.id : null);
  }, [B]);

  useEffect(() => {
    if (!selId) { setDoc(null); return undefined; }
    let alive = true;
    setDoc(null);
    api.cimBookDocument(engagementId, selId).then((r) => { if (alive) setDoc(r); })
      .catch((e) => { if (alive) toast({ type: 'error', title: 'Could not open the document', body: bkErr(e).detail }); });
    return () => { alive = false; };
  }, [engagementId, selId]);

  const finish = useCallback(async (job) => {
    setLive(null);
    if (job.status === 'completed') toast({ type: 'success', title: 'Book rendered' });
    else if (job.status === 'failed') toast({ type: 'error', title: 'Render failed', body: job.error || undefined });
    else toast({ type: 'info', title: 'Render cancelled' });
    const r = await load();
    if (r && job.status === 'completed' && (r.documents || []).length) setSelId(r.documents[0].id);
  }, [load]);

  // Progress lines every 5 s while a render runs; stops when it ends.
  window.usePolling(async () => {
    const cur = liveRef.current;
    if (!cur) return 'idle';
    const last = cur.events.length ? cur.events[cur.events.length - 1].id : 0;
    const r = await api.cimLedgerJob(engagementId, cur.jobId, { after: last, eventsOnly: true });
    const now = liveRef.current;
    if (!now || now.jobId !== cur.jobId) return 'gone';
    const evs = (r && r.events) || [];
    setLive((c) => (c && c.jobId === cur.jobId ? { ...c, job: { ...c.job, status: r.status, error: r.error }, events: bkMergeEvents(c.events, evs) } : c));
    if (r && BK_TERMINAL.has(r.status)) finish({ id: cur.jobId, status: r.status, error: r.error });
    return `${r ? r.status : ''}:${evs.length ? evs[evs.length - 1].id : last}`;
  }, {
    active: !!live, key: live ? live.jobId : null, first: 5000, stepAfter: 60 * 60 * 1000,
    giveUpAfter: 0, immediate: true, signature: (s) => s,
  });

  const saveSettings = async (patch) => {
    setBusy(true);
    try {
      await api.cimBookSettings(engagementId, patch);
      toast({ type: 'success', title: 'Book settings saved' });
      await load();
      return true;
    } catch (e) {
      toast({ type: 'error', title: 'Could not save', body: bkErr(e).detail });
      return false;
    } finally {
      setBusy(false);
    }
  };
  const render = async () => {
    setBusy(true);
    try {
      const r = await api.cimBookRender(engagementId);
      setPreview(false);
      setLive({ jobId: r.job_id, job: { id: r.job_id, status: r.status }, events: [] });
      await load();
    } catch (e) {
      const er = bkErr(e);
      toast({ type: 'error', title: er.status === 409 ? 'Not now' : 'Could not start the render', body: er.detail });
      load();
    } finally {
      setBusy(false);
    }
  };
  const cancel = async () => {
    const cur = liveRef.current;
    if (!cur) return;
    const ok = window.showConfirm ? await window.showConfirm({ title: 'Cancel the render?', confirmLabel: 'Cancel render', cancelLabel: 'Keep running', danger: true }) : true;
    if (!ok) return;
    try {
      const j = await api.cimLedgerCancel(engagementId, cur.jobId);
      finish({ id: cur.jobId, status: j.status, error: j.error });
    } catch (e) {
      toast({ type: 'error', title: 'Could not cancel', body: bkErr(e).detail });
      load();
    }
  };
  const download = async (f) => {
    try {
      await api.openCimBookFile(engagementId, f.id);
    } catch (e) {
      toast({ type: 'error', title: 'Could not download', body: bkErr(e).detail });
    }
  };

  // 7c: the open document again (its buyer PDF, uploads, approvals and final checks), and the list.
  const reloadDoc = useCallback(async () => {
    const id = selRef.current;
    if (!id) return null;
    const r = await api.cimBookDocument(engagementId, id);
    if (selRef.current === id) setDoc(r);
    return r;
  }, [engagementId]);
  const refresh = async () => {
    await Promise.all([reloadDoc().catch(() => null), load()]);
  };

  // While a team PDF's audit is queued or running: its job every 5 s (the light read; the
  // document read writes an access-log row and recomputes the final checks), and the
  // document again when one ends.
  window.usePolling(async () => {
    const ids = bkAuditJobs(doc);
    const jobs = await Promise.all(ids.map((id) => (id
      ? api.cimLedgerJob(engagementId, id, { eventsOnly: true }).catch(() => null) : Promise.resolve(null))));
    if (jobs.some((j) => !j || BK_TERMINAL.has(j.status))) {
      const r = await reloadDoc();
      if (r && !bkAuditing(r)) load();
    } else {
      const running = new Set(ids.filter((id, i) => jobs[i].status !== 'queued'));
      if (running.size) {
        setDoc((d) => (d && d.uploads ? { ...d, uploads: d.uploads.map((u) => (running.has(u.job_id) && u.audit_state === 'queued'
          ? { ...u, audit_state: 'running' } : u)) } : d));
      }
    }
    return jobs.map((j, i) => `${ids[i]}:${j ? j.status : ''}`).join(',');
  }, { active: bkAuditing(doc), key: doc && doc.document ? doc.document.id : null, first: 5000, signature: (x) => x });

  const docId = () => (doc && doc.document ? doc.document.id : null);
  const upload = async (file) => {
    const why = bkPdfRefusal(file);
    if (why) { toast({ type: 'error', title: why }); return; }
    setBusy(true);
    try {
      await api.uploadCimBookPdf(engagementId, docId(), file);
      toast({ type: 'success', title: 'Team PDF uploaded' });
      await refresh();
    } catch (e) {
      toast({ type: 'error', title: 'Could not upload', body: bkErr(e).detail });
    } finally {
      setBusy(false);
    }
  };
  const auditAgain = async (u) => {
    setBusy(true);
    try {
      await api.cimBookAuditAgain(engagementId, docId(), u.id);
      toast({ type: 'success', title: 'Audit queued' });
      await refresh();
    } catch (e) {
      toast({ type: 'error', title: 'Could not queue the audit', body: bkErr(e).detail });
      refresh();
    } finally {
      setBusy(false);
    }
  };
  const use = async (choice, reason) => {
    setBusy(true);
    try {
      await api.cimBookSwitch(engagementId, docId(), { source: choice.source, id: choice.id, reason });
      setDialog(null);
      toast({ type: 'success', title: 'Buyer PDF changed' });
      await refresh();
    } catch (e) {
      toast({ type: 'error', title: 'Could not change the buyer PDF', body: bkErr(e).detail });
      refresh();
    } finally {
      setBusy(false);
    }
  };
  const approve = async (ids, reason) => {
    setBusy(true);
    try {
      await api.cimBookApproval(engagementId, docId(), bkApproveBody(doc, ids, reason));
      setDialog(null);
      toast({ type: 'success', title: 'Final approval recorded' });
      await refresh();
    } catch (e) {
      const x = bkErr(e);
      if (x.status === 409 && x.body && Array.isArray(x.body.warnings)) {
        setDialog({ kind: 'approve', final: { ...(doc.final || {}), blocks: x.body.blocks || [], warnings: x.body.warnings }, changed: true });
        reloadDoc().catch(() => null);
      } else {
        toast({ type: 'error', title: 'Could not approve', body: x.detail });
        if (x.status === 409) { setDialog(null); refresh(); }
      }
    } finally {
      setBusy(false);
    }
  };
  const withdraw = async (reason) => {
    setBusy(true);
    try {
      await api.cimBookApproval(engagementId, docId(), { action: 'withdraw', reason });
      setDialog(null);
      toast({ type: 'success', title: 'Final approval withdrawn' });
      await refresh();
    } catch (e) {
      toast({ type: 'error', title: 'Could not withdraw', body: bkErr(e).detail });
      refresh();
    } finally {
      setBusy(false);
    }
  };
  const acts = {
    upload, auditAgain,
    use: (choice) => setDialog({ kind: 'use', choice }),
    approve: () => setDialog({ kind: 'approve', final: doc && doc.final, changed: false }),
    withdraw: () => setDialog({ kind: 'withdraw' }),
  };

  const wrap = (body) => (
    <div style={{ height: '100%', overflowY: 'auto' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>{body}</div>
    </div>
  );
  if (!B) {
    return wrap(err
      ? <div style={{ ..._bkCard, padding: '18px 16px', fontSize: 13, color: 'var(--rose-fg)' }}>{err.detail}</div>
      : <window.SkeletonRows rows={6} />);
  }
  const gate = bkRenderGate(B);
  const topBar = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <BkPill pair={bkState(BK_KIND, (B.preview || {}).kind)} />
      {(B.preview || {}).snapshot_version != null && <window.Pill hue="slate" sz="sm">{`Locked numbers v${B.preview.snapshot_version}`}</window.Pill>}
      <span style={{ flex: 1 }} />
      {!gate.ok && gate.why && <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{gate.why}</span>}
      {B.can_render && (
        <window.Btn size="sm" variant="accent" disabled={busy || !B.enabled || !!B.active_job || !!live}
          icon={window.Icon && window.Icon.Play ? <window.Icon.Play size={12} /> : null} onClick={() => setPreview(true)}>Render book</window.Btn>
      )}
    </div>
  );
  const last = B.last_job && B.last_job.status === 'failed' && !live ? (
    <div style={{ ..._bkCard, padding: '10px 14px', display: 'flex', gap: 8, alignItems: 'center', borderColor: 'var(--rose-fg)' }}>
      <BkPill pair={bkState(BK_JOB, 'failed')} />
      <span style={{ fontSize: 12.5, color: 'var(--text-2)', overflowWrap: 'anywhere' }}>{B.last_job.error || 'Render failed'}</span>
    </div>
  ) : null;
  return (
    <>
      {wrap(
        <>
          {topBar}
          {last}
          {live && <BkLive live={live} canCancel={!!B.can_render} onCancel={cancel} phone={phone} />}
          <BkSettings B={B} busy={busy} onSave={saveSettings} phone={phone} />
          <BkDocuments docs={B.documents} selId={selId} onSelect={(d) => setSelId(d.id)} phone={phone} />
          {selId && (doc ? <BkDocument engagementId={engagementId} doc={doc} phone={phone} onDownload={download} B={B} busy={busy} acts={acts} />
            : <window.SkeletonRows rows={4} />)}
        </>,
      )}
      {preview && <BkPreview B={B} busy={busy} onRender={render} onClose={() => setPreview(false)} phone={phone} />}
      {dialog && dialog.kind === 'use' && doc && (
        <BkReasonDrawer title="Use as the buyer PDF" subject={`${dialog.choice.label} · ${dialog.choice.name || ''}`} confirmLabel="Use this"
          max={300} busy={busy} onConfirm={(r) => use(dialog.choice, r)} onClose={() => setDialog(null)} phone={phone} />
      )}
      {dialog && dialog.kind === 'withdraw' && doc && (
        <BkReasonDrawer title="Withdraw final approval" subject={bkApprovalLine(doc.approval)} confirmLabel="Withdraw"
          max={500} busy={busy} onConfirm={withdraw} onClose={() => setDialog(null)} phone={phone} />
      )}
      {dialog && dialog.kind === 'approve' && doc && (
        <BkApproveDrawer doc={doc} final={dialog.final} changed={dialog.changed} busy={busy} onConfirm={approve}
          onClose={() => setDialog(null)} phone={phone} />
      )}
    </>
  );
};

window.CimBookTab = CimBookTab;
